import {
  WaypointLabel,
  WaypointLabelRender,
  WaypointPosition,
} from "@/components/r3f/renderers/waypoint-label-render";
import { PlaceholderHoverTooltip } from "@/components/r3f/utils/placeholder-hover-tooltip";
import { IElementWaypoint } from "@/hooks/use-placeholders";
import { useViewOverlayRef } from "@/hooks/use-view-overlay-ref";
import { TransformOverrides } from "@faro-lotv/project-source";
import type { JSX } from "react";

type SheetWaypointsTooltipLabelProps = {
  /** The hovered waypoint */
  hoveredWaypoint: WaypointPosition | undefined;
  /** Tooltip to show for the hovered waypoint */
  hoverTooltip: string | undefined;
  /** Optional transform overrides to select the position of the hovered waypoint */
  transformOverrides: TransformOverrides | undefined;
  /** Whether waypoint labels should be displayed */
  shouldDisplayWaypointLabels: boolean | undefined;
  /** Whether this is being rendered in the minimap */
  isInMinimap: boolean | undefined;
  /** Whether the waypoints should be visible */
  shouldWayPointsBeVisible: boolean;
  /** Callback issued when a placeholder is clicked */
  onPlaceholderClicked?(el: IElementWaypoint, event: MouseEvent): void;
  /** List of waypoints positions */
  waypoints: WaypointPosition[];
};

/** @returns a component to render the the waypoints labels and the tooltip of the hovered waypoint */
export function SheetWaypointsTooltipLabel({
  hoveredWaypoint,
  hoverTooltip,
  transformOverrides,
  shouldDisplayWaypointLabels,
  isInMinimap,
  shouldWayPointsBeVisible,
  onPlaceholderClicked,
  waypoints,
}: SheetWaypointsTooltipLabelProps): JSX.Element {
  const labelContainer = useViewOverlayRef();

  return (
    <>
      {/* Hover Tooltip for Waypoints */}
      {hoveredWaypoint && hoverTooltip && (
        <PlaceholderHoverTooltip
          placeholder={hoveredWaypoint.placeHolder}
          transformOverrides={transformOverrides}
        >
          {hoverTooltip}
        </PlaceholderHoverTooltip>
      )}

      {/* Label Rendering Logic */}
      {shouldDisplayWaypointLabels && (
        <>
          {/* Main View Labels */}
          {!isInMinimap && (
            <>
              {/* Render labels for waypoints if visible */}
              {shouldWayPointsBeVisible && (
                <WaypointLabelRender
                  waypoints={waypoints}
                  onLabelClick={onPlaceholderClicked}
                />
              )}
            </>
          )}

          {/* Minimap View Hovered Labels */}
          {isInMinimap && (
            <>
              {/* Render hovered waypoint label if visible */}
              {hoveredWaypoint && shouldWayPointsBeVisible && (
                <WaypointLabel
                  key={hoveredWaypoint.placeHolder.id}
                  placeHolder={hoveredWaypoint.placeHolder}
                  position={hoveredWaypoint.renderPosition}
                  parentRef={labelContainer}
                  disablePointerEvents
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );
}
